import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as Sentry from '@sentry/react';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          bgcolor="#FAFAF8"
        >
          <Box sx={{ width: 440, border: BORDER, bgcolor: '#FFFFFF', p: 5, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                border: BORDER,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 28, color: ACCENT }} />
            </Box>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#1A1A1A',
                mb: 1,
              }}
            >
              Something went wrong
            </Typography>
            <Typography sx={{ color: '#6B6560', fontSize: '0.85rem', lineHeight: 1.6, mb: 3 }}>
              An unexpected error occurred. Please try refreshing the page.
            </Typography>
            {this.state.error && (
              <Box
                component="pre"
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: '#F5F5F0',
                  border: BORDER,
                  fontSize: '0.7rem',

                  color: '#1A1A1A',
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                {this.state.error.message}
              </Box>
            )}
            <Button
              variant="outlined"
              onClick={this.handleRetry}
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 0,
                borderColor: ACCENT,
                color: ACCENT,

                fontSize: '0.75rem',
              }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
